// Institutional-grade register page for UniFed
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Shield } from 'lucide-react';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Password mismatch detected');
      return;
    }

    if (formData.password.length < 12) {
      setError('Password requirement: minimum 12 characters');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      await register(registerData);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Contact system administrator.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0B0F14]">
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'linear-gradient(#222A35 1px, transparent 1px), linear-gradient(90deg, #222A35 1px, transparent 1px)',
        backgroundSize: '50px 50px'
      }}></div>
      
      <div className="relative z-10 w-full max-w-2xl px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-[#11161D] border border-[#222A35]">
            <Shield className="w-8 h-8 text-[#4A6B8C]" strokeWidth={1.5} />
          </div>
          
          <h1 className="text-5xl font-bold mb-3 tracking-tight" style={{ fontFamily: 'IBM Plex Sans' }}>
            UniFed
          </h1>
          
          <p className="text-xl text-[#8A96A3] mb-4" style={{ fontFamily: 'Inter' }}>
            New Account Registration
          </p>
          
          <p className="text-xs uppercase tracking-widest text-[#5C6A7A] font-mono">
            Enrollment requires administrator approval
          </p>
        </div>

        <div className="space-y-6">
          <div className="border-t border-[#222A35] pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-[#7D3B3B] border border-[#f87171] px-4 py-3 text-sm">
                  <p className="text-[#f87171] font-mono">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-[#2E5E4E] border border-[#4ade80] px-4 py-3 text-sm">
                  <p className="text-[#4ade80] font-mono">REGISTRATION SUCCESSFUL. REDIRECTING...</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-xs uppercase tracking-widest text-[#8A96A3] mb-2 font-medium">
                    First Name
                  </label>
                  <input
                    id="first_name"
                    name="first_name"
                    data-testid="register-firstname-input"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full input-field"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-xs uppercase tracking-widest text-[#8A96A3] mb-2 font-medium">
                    Last Name
                  </label>
                  <input
                    id="last_name"
                    name="last_name"
                    data-testid="register-lastname-input"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full input-field"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-xs uppercase tracking-widest text-[#8A96A3] mb-2 font-medium">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="register-email-input"
                  placeholder="name@organization.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full input-field font-mono text-sm"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-xs uppercase tracking-widest text-[#8A96A3] mb-2 font-medium">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="register-password-input"
                  placeholder="Min 12 chars: A-Z, a-z, 0-9, special"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full input-field font-mono text-sm"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs uppercase tracking-widest text-[#8A96A3] mb-2 font-medium">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  data-testid="register-confirm-password-input"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full input-field font-mono text-sm"
                />
              </div>

              <button
                type="submit"
                data-testid="register-submit-button"
                className="w-full btn-primary mt-6"
                disabled={loading || success}
              >
                {loading ? 'PROCESSING...' : success ? 'REGISTERED' : 'REGISTER'}
              </button>
            </form>
          </div>

          <div className="border-t border-[#222A35] pt-4">
            <p className="text-sm text-[#5C6A7A] text-center">
              Already enrolled?{' '}
              <Link to="/login" className="text-[#4A6B8C] hover:text-[#E4E7EB] transition-colors font-medium">
                Return to login
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-12 text-center">
          <p className="text-xs text-[#5C6A7A] uppercase tracking-widest font-mono">
            Zero-trust architecture enforced
          </p>
        </div>
      </div>
    </div>
  );
};
